import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { message } from "antd";

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import useCustomersData from './useCustomersData';
import MobileSheet from './MobileSheet';
import { formatKes, initialsOf, pluralize } from './format';
import './MobileCustomers.css';

const STATUS = {
    active: { label: "Active", color: "#1a9e6b" },
    arrears: { label: "In arrears", color: "#e8452c" },
    inactive: { label: "Inactive", color: "#8fa7bd" },
};

const FILTERS = [
    { key: "all", label: "All" },
    { key: "active", label: "Active" },
    { key: "arrears", label: "In arrears" },
    { key: "inactive", label: "Inactive" },
];

/* `field` marks options that need a loan field the API doesn't send
   yet — they only render once that field shows up in the data. */
const SORTS = [
    { key: "az", label: "A–Z" },
    { key: "recent", label: "Recently added" },
    { key: "balance", label: "Balance: High to Low" },
    { key: "due", label: "Next payment due", field: "nextDueDate" },
    { key: "overdue", label: "Days overdue", field: "daysOverdue" },
];

const compareBy = {
    az: (a, b) => a.name.localeCompare(b.name),
    recent: (a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""),
    balance: (a, b) => b.balance - a.balance,
    // Customers with no due date go last.
    due: (a, b) => (a.nextDueDate || "9999").localeCompare(b.nextDueDate || "9999"),
    // Descending, so customers with 0 days overdue naturally go last.
    overdue: (a, b) => b.daysOverdue - a.daysOverdue,
};

const loansLabel = (count) => {
    if (count === 0) return "No loans";
    if (count === 1) return "1 loan";
    return `${count} loans`;
};

const SearchIcon = () => (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
        <circle cx="8.5" cy="8.5" r="5.5" stroke="#8fa7bd" strokeWidth="2" />
        <line x1="13" y1="13" x2="17.5" y2="17.5" stroke="#8fa7bd" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

const MobileCustomers = () => {
    const navigate = useNavigate();
    const { isLoading, isError, refetch, counts, customers } = useCustomersData();

    const [filter, setFilter] = useState("all");
    const [sort, setSort] = useState("az");
    const [sortOpen, setSortOpen] = useState(false);
    const [search, setSearch] = useState("");

    // Loan fields are undefined until the API ships them; anything that
    // depends on one stays hidden until at least one row carries it.
    const has = (field) => customers.some(customer => customer[field] !== undefined);

    const filters = FILTERS.filter(({ key }) => key !== "arrears" || has("inArrears"));
    const sorts = SORTS.filter(option => !option.field || has(option.field));

    const query = search.trim().toLowerCase().replace(/\s+/g, '');
    const visible = customers
        .filter(customer => filter === "all" || customer.status === filter)
        .filter(customer =>
            !query ||
            customer.name.toLowerCase().replace(/\s+/g, '').includes(query) ||
            (customer.telephone || '').replace(/\s+/g, '').includes(query) ||
            String(customer.customerId || '').toLowerCase().replace(/\s+/g, '').includes(query)
        )
        .sort(compareBy[sort]);

    return (
        <div className="mobile-customers">
            <header className="mobile-customers-header">
                <div className="mobile-customers-title-row">
                    <div className="mobile-customers-title-left">
                        <button
                            type="button"
                            className="mobile-customers-back"
                            aria-label="Back"
                            onClick={() => navigate('/dashboard/stats')}
                        >
                            <ArrowBackIosNewIcon className="mobile-customers-back-icon" />
                        </button>
                        <h1 className="mobile-customers-title">Customers</h1>
                    </div>

                    {/* Gated until the mobile customer-creation designs are ready. */}
                    <button
                        type="button"
                        className="mobile-customers-add"
                        aria-label="New customer"
                        onClick={() => message.info('Coming soon')}
                    >
                        <AddIcon className="mobile-customers-add-icon" />
                    </button>
                </div>

                <label className="mobile-customers-search">
                    <SearchIcon />
                    <input
                        type="search"
                        value={search}
                        onChange={event => setSearch(event.target.value)}
                        placeholder="Search name, phone or ID"
                    />
                </label>

                <div className="mobile-customers-chips">
                    {filters.map(({ key, label }) => (
                        <button
                            type="button"
                            key={key}
                            className={[
                                "mobile-customers-chip",
                                key === "arrears" ? "arrears" : "",
                                key === filter ? "selected" : "",
                            ].join(' ')}
                            onClick={() => setFilter(key)}
                        >
                            {counts ? `${label} · ${counts[key]}` : label}
                        </button>
                    ))}
                </div>
            </header>

            <div className="mobile-customers-list">
                <div className="mobile-customers-meta">
                    <span className="mobile-customers-count">
                        {!isLoading && !isError && pluralize(visible.length, "customer")}
                    </span>
                    <button
                        type="button"
                        className="mobile-customers-sort-trigger"
                        onClick={() => setSortOpen(true)}
                    >
                        Sort: {SORTS.find(option => option.key === sort).label}
                        <KeyboardArrowDownIcon className="mobile-customers-sort-caret" />
                    </button>
                </div>

                <div className="mobile-customers-card">
                    {isLoading && [1, 2, 3, 4, 5].map(n => (
                        <div className="mobile-customers-row" key={n}>
                            <span className="mobile-customers-avatar" />
                            <span className="mobile-customers-text">
                                <span className="mobile-customers-skeleton wide" />
                                <span className="mobile-customers-skeleton" />
                            </span>
                        </div>
                    ))}

                    {isError && (
                        <div className="mobile-customers-error">
                            Couldn't load customers
                            <button
                                type="button"
                                className="mobile-customers-retry"
                                onClick={() => refetch()}
                            >
                                Retry
                            </button>
                        </div>
                    )}

                    {!isLoading && !isError && visible.length === 0 && (
                        <div className="mobile-customers-empty">No customers match</div>
                    )}

                    {!isLoading && !isError && visible.map(customer => (
                        <Link
                            to={`/dashboard/customer/${customer.id}`}
                            className="mobile-customers-row"
                            key={customer.id}
                        >
                            <span className="mobile-customers-avatar">{initialsOf(customer.name)}</span>
                            <span className="mobile-customers-text">
                                <span className="mobile-customers-name">{customer.name}</span>
                                <span className="mobile-customers-sub">
                                    {customer.telephone}
                                    {customer.loanCount !== undefined && ` · ${loansLabel(customer.loanCount)}`}
                                </span>
                            </span>
                            <span className="mobile-customers-right">
                                <span className="mobile-customers-balance">{formatKes(customer.balance)}</span>
                                <span
                                    className="mobile-customers-status"
                                    style={{ color: STATUS[customer.status].color }}
                                >
                                    {customer.statusLabel}
                                </span>
                            </span>
                        </Link>
                    ))}
                </div>
            </div>

            <MobileSheet
                open={sortOpen}
                onClose={() => setSortOpen(false)}
                title="Sort by"
                options={sorts}
                selected={sort}
                onSelect={setSort}
            />
        </div>
    );
};

export default MobileCustomers;
